import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { toast } from "sonner";
import { AcademicAnalytics } from "./AdminDashboard";
import EventsPanel from "./EventsPanel";
import EducatorPonderrLearn from "./ponderr_learn/EducatorPonderrLearn";
import AlertsInbox from "./shared/AlertsInbox";
import AttendanceScanner from "./student_id/AttendanceScanner";
import { Check, X, Save, BookOpen, Lock, Plus, Trash2, Video, Link as LinkIcon, FileText, Radio, Send, ClipboardCheck, Printer, Download, Wallet, ScanLine } from "lucide-react";

// Coerce a FastAPI error (which may be a 422 validation-error array/object) into
// a safe display string — prevents "Objects are not valid as a React child".
const errText = (err, fb = "Failed") => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => (x && (x.msg || x.message)) || JSON.stringify(x)).join(", ") || fb;
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return d || err?.message || fb;
};

const BASE_TABS = [
  {id:"attendance", label:"Attendance"},
  {id:"marks",      label:"Marks Cards"},
  {id:"marks_cards",label:"Report Cards",
    disabled: true, title: "Consolidated under Marks Cards"},
  {id:"analytics",  label:"Class Analytics"},
  {id:"events",     label:"Events"},
  {id:"lms",        label:"LMS (My Learnings)"},
  {id:"ponderr",    label:"Ponderr"},
  {id:"ponderr_learn", label:"PonderrLearn"},
  {id:"review_queue", label:"Review Queue"},
  {id:"lms_perf",   label:"LMS Performance"},
  {id:"schedule",   label:"Schedule"},
  {id:"alerts",     label:"Alerts"},
];

function EducatorAnalytics({ profile }) {
  // Iter22 — Educator's own class-section analytics. Reuses the shared
  // AcademicAnalytics component but filters to the educator's assigned bucket.
  const [testTypes, setTestTypes] = useState([]);
  const [testType, setTestType] = useState("");
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);
  useEffect(()=>{
    api.get("/settings").then(r => {
      const tt = r.data?.test_types || [];
      setTestTypes(tt); setTestType(tt[0] || "");
    });
  }, []);
  useEffect(()=>{
    if (!profile?.class_name || !profile?.section) { setRow(null); return; }
    setBusy(true);
    api.get("/analytics/academic", { params: testType ? { test_type: testType } : {} })
      .then(r => {
        const mine = (r.data || []).find(b =>
          b.class_name === profile.class_name && b.section === profile.section);
        setRow(mine || null);
      })
      .catch(() => setRow(null))
      .finally(() => setBusy(false));
  }, [testType, profile?.class_name, profile?.section]);
  if (!profile?.class_name || !profile?.section) {
    return <div className="nb-card p-6 text-center text-slate-500">
      Your Educator profile isn&apos;t linked to any class/section. Ask the Admin to assign one on the Educators tab.
    </div>;
  }
  return (
    <div className="space-y-4" data-testid="edu-analytics-panel">
      <div className="nb-card p-4 bg-[#C4B5FD] flex items-end gap-3 flex-wrap">
        <div>
          <div className="display text-lg font-bold">Class Analytics — {profile.class_name}-{profile.section}</div>
          <p className="text-xs">Academic + attendance analytics for your assigned class. Same data the School Admin &amp; Facilitator see, scoped to your bucket only.</p>
        </div>
        <div className="ml-auto"><label className="nb-label">Test Type</label>
          <select className="nb-select mt-1 w-40" value={testType}
                  onChange={e=>setTestType(e.target.value)}
                  data-testid="edu-aa-test-type">
            <option value="">All Test Types</option>
            {testTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
      </div>
      {busy && <div className="mono">Loading…</div>}
      {!busy && !row && (
        <div className="nb-card p-6 text-center text-slate-500">
          No marks logged yet for this Test Type in {profile.class_name}-{profile.section}.
        </div>
      )}
      {row && (
        <div className="nb-card p-5 space-y-3" data-testid={`edu-aa-card-${row.class_name}-${row.section}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="display text-lg font-bold">{row.class_name} · Section {row.section}</div>
              <div className="text-xs text-slate-600">{row.students_count} students · {testType || "All types"}</div>
            </div>
            <div className="text-right">
              <div className="mono font-bold text-3xl">{row.avg_pct}%</div>
              <div className="text-[10px]">CLASS AVERAGE</div>
            </div>
          </div>
          <div className="nb-card-sm p-3 bg-[#FDE047]">
            <div className="nb-label">Best Subject</div>
            <div className="font-bold text-sm">{row.best_subject || "—"} · {row.best_subject_avg || 0}% avg</div>
          </div>
          <details open={expanded}>
            <summary onClick={e=>{e.preventDefault(); setExpanded(x=>!x);}}
                     className="cursor-pointer text-xs font-bold uppercase">
              {expanded ? "▾ Hide" : "▸ Show"} highest score per subject & top 10 students
            </summary>
            {expanded && (
              <div className="pt-3 space-y-3">
                <div>
                  <div className="nb-label mb-1">Highest Score per Subject</div>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left border-b border-slate-300">
                      <th className="py-1">Subject</th><th>Top Marks</th><th>Student</th>
                    </tr></thead>
                    <tbody>
                      {row.subject_bests.map(sb => (
                        <tr key={sb.subject} className="border-b border-slate-200">
                          <td className="py-1">{sb.subject}</td>
                          <td className="mono">{sb.top_marks}/{sb.top_max}</td>
                          <td>{sb.top_student} ({sb.roll_no})</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="nb-label mb-1">Top 10 Students</div>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left border-b border-slate-300">
                      <th className="py-1">Rank</th><th>Name</th><th>Roll</th><th>%</th><th>Grade</th>
                    </tr></thead>
                    <tbody>
                      {row.top_students.map((s, i) => (
                        <tr key={s.student_id} className="border-b border-slate-200"
                            data-testid={`edu-aa-top-${i+1}`}>
                          <td className="py-1 font-bold">{i+1}</td>
                          <td>{s.name}</td><td className="mono">{s.roll_no}</td>
                          <td className="mono">{s.pct}%</td>
                          <td><span className="nb-badge">{s.grade}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </details>
        </div>
      )}
    </div>
  );
}

export default function EducatorDashboard() {
  const [tab, setTab] = useState("attendance");
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState({grades:[], sections:[]});
  useEffect(() => {
    api.get("/educator/me").then(r => setProfile(r.data)).catch(()=>setProfile({}));
    api.get("/settings").then(r => setSettings(r.data)).catch(()=>{});
  }, []);
  const tabs = settings.educator_payroll_visible !== false
    ? [...BASE_TABS, {id:"my_payrolls", label:"My Payrolls"}]
    : BASE_TABS;
  if (profile === null) return <Shell title="Educator Console" tabs={tabs} active={tab} onTab={setTab}>
    <div className="mono">Loading…</div></Shell>;
  return (
    <Shell title="Educator Console"
           subtitle={profile.subject ? `${profile.subject} · Class ${profile.class_name||"—"}-${profile.section||"—"}` : "Not assigned"}
           tabs={tabs} active={tab} onTab={setTab}>
      {profile.class_name && profile.section && (
        <div className="nb-card p-4 mb-5 bg-[#C4B5FD] flex items-center gap-3">
          <BookOpen size={20}/>
          <div className="text-sm"><span className="nb-label">Your Default Assignment</span>
            <div className="font-bold">Class {profile.class_name} · Section {profile.section} · {profile.subject}</div></div>
        </div>
      )}
      {tab==="attendance" && <Attendance profile={profile} settings={settings}/>}
      {tab==="marks" && <Marks profile={profile} settings={settings}/>}
      {tab==="marks_cards" && <MarksCards profile={profile} settings={settings}/>}
      {tab==="lms" && <LMS profile={profile} settings={settings}/>}
      {tab==="analytics" && <EducatorAnalytics profile={profile}/>}
      {tab==="events" && <EventsPanel mode="execute"/>}
      {tab==="schedule" && <Schedule/>}
      {tab==="ponderr" && <EducatorPonderr/>}
      {tab==="ponderr_learn" && <EducatorPonderrLearn profile={profile}/>}
      {tab==="review_queue" && <EducatorReviewQueue/>}
      {tab==="lms_perf" && <EducatorLMSPerformance/>}
      {tab==="my_payrolls" && <MyPayrolls/>}
      {tab==="alerts" && <AlertsInbox/>}
    </Shell>
  );
}

function MyPayrolls() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/payroll/my")
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, []);
  const download = async (pid, name, month) => {
    try {
      const res = await api.get(`/payroll/${pid}/payslip-pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `payslip_${(name||"staff").replace(/\s+/g,"_")}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Payslip unavailable"); }
  };
  if (busy) return <div className="mono">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="my-payrolls-panel">
      <div className="nb-card p-4 bg-[#93C5FD]">
        <div className="display text-lg font-bold">My Payrolls</div>
        <p className="text-xs">Approved payslips from the Accountant &amp; Admin/Founder. Older / rejected months will not appear here.</p>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Month</th><th>Slab</th><th>Gross</th><th>Deductions</th><th>Net</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.payroll_id} data-testid={`my-payroll-row-${r.payroll_id}`}>
                <td className="mono">{r.month}</td>
                <td>{r.slab_name || "—"}</td>
                <td className="mono">₹{(r.gross||0).toLocaleString()}</td>
                <td className="mono">₹{(r.total_deductions||0).toLocaleString()}</td>
                <td className="mono font-bold">₹{(r.net||0).toLocaleString()}</td>
                <td><span className="nb-badge">{(r.status||"").toUpperCase()}</span></td>
                <td>
                  <button onClick={()=>download(r.payroll_id, r.beneficiary_name || r.educator_name, r.month)}
                          className="nb-btn nb-btn-secondary text-xs"
                          data-testid={`my-payroll-dl-${r.payroll_id}`}>
                    <Download size={12}/> Download
                  </button>
                </td>
              </tr>
            ))}
            {rows.length===0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-500">
                No approved payslips yet. Contact your Accountant once payroll has been pushed &amp; approved.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- LMS (My Learnings) ---------- */
const KIND_OPTS = [
  {v:"video", n:"Video",       Icon:Video},
  {v:"live",  n:"Live Class",  Icon:Radio},
  {v:"link",  n:"Link/Course", Icon:LinkIcon},
  {v:"note",  n:"Note",        Icon:FileText},
];

function LMS({ profile, settings }) {
  const [rows, setRows] = useState([]);
  const grades = settings?.grades || [];
  const sections = settings?.sections || [];
  const subjects = settings?.subjects || [];
  const empty = {title:"", kind:"video", url:"", description:"",
                 class_name: profile?.class_name || "",
                 section: profile?.section || "",
                 subject: profile?.subject || ""};
  const [f, setF] = useState(empty);
  const load = useCallback(() => api.get("/educator/learnings").then(r => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    if (!f.class_name || !f.section) return toast.error("Pick a grade & section");
    try {
      await api.post("/educator/learnings", f);
      toast.success("Resource added — visible to your class students");
      setF({...empty}); load();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this resource?")) return;
    await api.delete(`/educator/learnings/${id}`); load();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="nb-card p-6 bg-[#FDE047] space-y-2 h-fit"
            data-testid="edu-learnings-form">
        <h3 className="display text-xl font-bold">Add Learning Resource</h3>
        <input className="nb-input" placeholder="Title" required value={f.title}
               onChange={e=>setF({...f, title:e.target.value})} data-testid="lrn-title"/>
        <select className="nb-select" value={f.kind}
                onChange={e=>setF({...f, kind:e.target.value})} data-testid="lrn-kind">
          {KIND_OPTS.map(k => <option key={k.v} value={k.v}>{k.n}</option>)}
        </select>
        <input className="nb-input" placeholder="URL (YouTube / Teams meeting / Google Drive)"
               value={f.url} onChange={e=>setF({...f, url:e.target.value})} data-testid="lrn-url"/>
        <textarea className="nb-textarea" placeholder="Short description / instructions"
                  value={f.description} onChange={e=>setF({...f, description:e.target.value})}/>
        {/* Iter30 — Grade / Section / Subject bound to School Admin settings */}
        <div className="grid grid-cols-3 gap-2">
          <select className="nb-select" value={f.class_name}
                  onChange={e=>setF({...f, class_name:e.target.value})}
                  data-testid="lrn-grade">
            <option value="">Grade…</option>
            {grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select className="nb-select" value={f.section}
                  onChange={e=>setF({...f, section:e.target.value})}
                  data-testid="lrn-section">
            <option value="">Section…</option>
            {sections.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="nb-select" value={f.subject}
                  onChange={e=>setF({...f, subject:e.target.value})}
                  data-testid="lrn-subject">
            <option value="">Subject…</option>
            {subjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button className="nb-btn w-full justify-center" data-testid="lrn-add">
          <Plus size={16}/> Publish to Class
        </button>
      </form>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
        {rows.map(x => {
          const K = KIND_OPTS.find(k=>k.v===x.kind) || KIND_OPTS[0];
          return (
            <div key={x.learning_id} className="nb-card p-4 bg-white"
                 data-testid={`lrn-card-${x.learning_id}`}>
              <div className="flex items-center gap-2"><K.Icon size={16}/>
                <span className="nb-label">{K.n}</span>
                <span className="nb-badge bg-[#93C5FD] ml-auto">{x.class_name}-{x.section}</span>
                {x.subject && <span className="nb-badge bg-[#FDE047] ml-1">{x.subject}</span>}
              </div>
              <h4 className="display text-lg font-bold mt-1">{x.title}</h4>
              {x.description && <p className="text-sm mt-1">{x.description}</p>}
              {x.url && (
                <a href={x.url} target="_blank" rel="noreferrer"
                   className="nb-btn nb-btn-secondary mt-2 inline-flex text-xs">
                  Open <LinkIcon size={12}/>
                </a>
              )}
              <button onClick={()=>del(x.learning_id)}
                      className="nb-btn nb-btn-danger px-2 py-1 text-xs mt-2 ml-2">
                <Trash2 size={12}/> Remove
              </button>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="nb-card p-6 text-slate-500 text-sm sm:col-span-2">
            You haven&apos;t added any resources yet. Uploads become visible to
            students in your assigned class.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Schedule (my class timetable, read-only) ---------- */
function Schedule() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/timetables/mine").then(r => setRows(r.data));
  }, []);
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"];
  const periods = Array.from(new Set(rows.map(r => r.period))).sort((a,b) => a - b);
  const grid = {};
  rows.forEach(r => { grid[`${r.day_of_week}|${r.period}`] = r; });
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#93C5FD]">
        <div className="display text-xl font-bold">Weekly Schedule</div>
        <p className="text-sm mt-1">Your class timetable — designed by the school admin.</p>
      </div>
      {rows.length === 0 ? (
        <div className="nb-card p-6 text-slate-500">
          No timetable set for your class yet. Ask the admin to design one.
        </div>
      ) : (
        <div className="nb-card p-0 overflow-x-auto">
          <table className="nb-table">
            <thead><tr><th>Period</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr></thead>
            <tbody>
              {periods.map(p => (
                <tr key={p}>
                  <td className="font-bold mono">P{p}</td>
                  {DAYS.map(d => {
                    const s = grid[`${d}|${p}`];
                    return <td key={d} className={s ? "bg-[#FDE047]" : ""}>
                      {s ? (
                        <div>
                          <div className="font-bold text-sm">{s.subject}</div>
                          {s.educator_name && <div className="text-xs text-slate-600">{s.educator_name}</div>}
                          {s.start_time && <div className="text-xs mono">{s.start_time}-{s.end_time}</div>}
                        </div>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function Attendance({ profile, settings }) {
  const TODAY = new Date().toISOString().slice(0,10);
  const [cls, setCls] = useState(profile.class_name || "");
  const [sec, setSec] = useState(profile.section || "");
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [useDate, setUseDate] = useState(TODAY);
  const [overrideActive, setOverrideActive] = useState(false);
  const [overrideExp, setOverrideExp] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const loadOverrides = async () => {
    try {
      const r = await api.get("/overrides/my");
      // Iter22: educator_attendance is the dedicated override; also accept
      // legacy student_attendance for backward compatibility.
      const eduOverride = r.data?.educator_attendance?.active
        || r.data?.student_attendance?.active;
      setOverrideActive(!!eduOverride);
      setOverrideExp(r.data?.educator_attendance?.expires_at
        || r.data?.student_attendance?.expires_at || null);
    } catch (e) { setOverrideActive(false); }
  };
  useEffect(()=>{ loadOverrides(); }, []);
  // If override switches off, snap the date back to today.
  useEffect(()=>{ if (!overrideActive) setUseDate(TODAY); }, [overrideActive, TODAY]);

  const load = async () => {
    if (!cls || !sec) { setStudents([]); return; }
    const s = await api.get("/students", { params: { class_name: cls, section: sec }});
    setStudents(s.data);
    const a = await api.get("/attendance", { params: { class_name: cls, section: sec, date: useDate }});
    const init = {};
    s.data.forEach(st => { init[st.student_id] = a.data.records?.[st.student_id] ?? true; });
    setRecords(init);
  };
  useEffect(()=>{ load(); }, [cls, sec, useDate]);  // eslint-disable-line

  const toggle = (id) => setRecords(r => ({...r, [id]: !r[id]}));
  const markAllPresent = () => {
    const next = {}; students.forEach(s => { next[s.student_id] = true; }); setRecords(next);
  };
  const save = async () => {
    if (!cls || !sec) return toast.error("Pick grade & section");
    try {
      const r = await api.post("/attendance",
        { class_name: cls, section: sec, date: useDate, records });
      toast.success(`Saved · ${r.data.absent_count ?? 0} parent alert(s) sent`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Locked / failed"); }
  };
  const presentCount = Object.values(records).filter(Boolean).length;
  const absentCount = students.length - presentCount;
  return (
    <div className="space-y-5">
      {overrideActive && (
        <div className="nb-card p-3 bg-[#FDE047] flex flex-wrap items-center gap-3" data-testid="att-override-banner">
          <Lock size={16}/>
          <div className="text-sm font-bold">
            Historical override ACTIVE — you may pick a past date. Expires {(overrideExp||"").slice(0,16).replace("T"," ")} UTC.
          </div>
        </div>
      )}
      <div className="nb-card p-5 flex flex-wrap gap-3 items-end">
        {/* Iter50 — Grade & Section locked to educator's assigned class UNLESS
            School Admin has granted a temporary override (auto-expires). */}
        {(() => {
          const unlocked = profile.override_expires_at &&
                           new Date(profile.override_expires_at) > new Date();
          const lockCls = !!profile.class_name && !unlocked;
          const lockSec = !!profile.section && !unlocked;
          return (<>
        <div><label className="nb-label flex items-center gap-1">
          {lockCls ? <><Lock size={11}/>Grade</> : "Grade"}</label>
          <select className="nb-input mt-1 w-32" value={cls}
                  onChange={e=>setCls(e.target.value)}
                  disabled={lockCls}
                  title={lockCls ? "Locked to your assigned class"
                        : unlocked ? `Temporarily unlocked until ${new Date(profile.override_expires_at).toLocaleString()}`
                        : ""}
                  data-testid="att-class">
            <option value="">--</option>
            {settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className="nb-label flex items-center gap-1">
          {lockSec ? <><Lock size={11}/>Section</> : "Section"}</label>
          <select className="nb-input mt-1 w-32" value={sec}
                  onChange={e=>setSec(e.target.value)}
                  disabled={lockSec}
                  title={lockSec ? "Locked to your assigned section"
                        : unlocked ? `Temporarily unlocked until ${new Date(profile.override_expires_at).toLocaleString()}`
                        : ""}
                  data-testid="att-section">
            <option value="">--</option>
            {settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
          </select></div>
        {unlocked && (
          <div className="nb-badge bg-[#86EFAC] text-[10px]" data-testid="att-override-badge">
            🔓 Override until {new Date(profile.override_expires_at).toLocaleString(undefined,{month:"short",day:"2-digit",hour:"2-digit",minute:"2-digit"})}
          </div>
        )}
          </>);
        })()}
        <div>
          <div className="nb-label flex items-center gap-1">
            {overrideActive ? "Date (override)" : <><Lock size={11}/>Date (locked to today)</>}
          </div>
          {overrideActive ? (
            <input type="date" className="nb-input mt-1 mono" max={TODAY}
                   value={useDate} onChange={e=>setUseDate(e.target.value)}
                   data-testid="att-date-picker"/>
          ) : (
            <div className="nb-input mt-1 bg-slate-100 cursor-not-allowed font-bold mono" data-testid="att-date">{TODAY}</div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={()=>setScanOpen(true)}
                  className="nb-btn nb-btn-secondary" data-testid="att-scan-open">
            <ScanLine size={14}/> Scan Attendance
          </button>
          <button onClick={markAllPresent} disabled={!students.length}
                  className="nb-btn nb-btn-secondary" data-testid="att-mark-all-present">
            <Check size={14}/> All Present
          </button>
          <button onClick={save} disabled={!students.length}
                  className="nb-btn" data-testid="att-save"><Save size={16}/>Save Attendance</button>
        </div>
      </div>
      {scanOpen && <AttendanceScanner onClose={()=>setScanOpen(false)} onMarked={load}/>}

      {students.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="nb-card-sm p-3 bg-[#86EFAC]">
            <div className="nb-label text-[10px]">Present</div>
            <div className="font-bold mono text-xl">{presentCount}</div>
          </div>
          <div className="nb-card-sm p-3 bg-[#FCA5A5]">
            <div className="nb-label text-[10px]">Absent</div>
            <div className="font-bold mono text-xl">{absentCount}</div>
          </div>
          <div className="nb-card-sm p-3 bg-white">
            <div className="nb-label text-[10px]">Total Students</div>
            <div className="font-bold mono text-xl">{students.length}</div>
          </div>
          <div className="nb-card-sm p-3 bg-[#FDE047]">
            <div className="nb-label text-[10px]">% Present</div>
            <div className="font-bold mono text-xl">
              {students.length ? Math.round(presentCount/students.length*100) : 0}%
            </div>
          </div>
        </div>
      )}

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex justify-between items-center">
          <h3 className="display text-lg font-bold">Roster — {useDate}</h3>
          <span className="text-xs">Tap a status to toggle. Save when done.</span>
        </div>
        <table className="nb-table">
          <thead><tr><th>Roll</th><th>Name</th><th>Status</th></tr></thead>
          <tbody>
            {students.map(s => {
              const present = !!records[s.student_id];
              return (
                <tr key={s.student_id} data-testid={`att-row-${s.student_id}`}>
                  <td className="mono">{s.roll_no}</td>
                  <td className="font-bold">{s.name}</td>
                  <td>
                    <button onClick={()=>toggle(s.student_id)}
                            className={`nb-btn ${present ? "nb-btn-secondary" : "nb-btn-danger"}`}
                            data-testid={`att-toggle-${s.student_id}`}>
                      {present ? <><Check size={14}/> Present</> : <><X size={14}/> Absent</>}
                    </button>
                  </td>
                </tr>
              );
            })}
            {students.length===0 && <tr><td colSpan={3} className="text-center py-6 text-slate-500">Pick a grade &amp; section</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Marks({ profile, settings }) {
  const [cls, setCls] = useState(profile.class_name || "");
  const [sec, setSec] = useState(profile.section || "");
  const [testType, setTestType] = useState(settings.test_types?.[0] || "FA1");
  const pushKind = "marks_card"; // Iter21: Type dropdown removed — Marks Cards is the single source
  const [maxM, setMaxM] = useState(100);
  const [minM, setMinM] = useState(35);
  const [splitCalc, setSplitCalc] = useState(true);
  const [gradesOnlyNS, setGradesOnlyNS] = useState(false);
  // Iter68 — per-subject Max/Passing overrides (up to 3). {subject:{max_marks,min_marks}}
  const [subjectOverrides, setSubjectOverrides] = useState({});
  const partA = settings.subjects_part_a || [];
  const partB = settings.subjects_part_b || [];
  const hasSeg = partA.length > 0 || partB.length > 0;
  const isScholastic = (sub) => !hasSeg ? true : (partA.includes(sub) || !partB.includes(sub));
  const gradeTiers = (settings.grading_scale || []).map(g => g.grade);
  const [students, setStudents] = useState([]);
  // Iter21: prefer Part A + Part B ordered lists; fall back to legacy `subjects`.
  const partAB = [...(settings.subjects_part_a||[]), ...(settings.subjects_part_b||[])];
  const allSubjects = partAB.length > 0 ? partAB : (settings.subjects || []);
  const [activeSubjects, setActiveSubjects] = useState([]);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const subjects = activeSubjects.length > 0
    ? allSubjects.filter(s => activeSubjects.includes(s))
    : allSubjects;
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null);

  useEffect(()=>{ setTestType(settings.test_types?.[0] || "FA1"); }, [settings.test_types]);

  // Iter19: Load Test Type Log whenever (class, section, testType) changes.
  // TTL contains persisted subjects + max/min + all previously entered marks.
  useEffect(()=>{
    (async () => {
      if (!cls || !sec) { setStudents([]); return; }
      const s = await api.get("/students", { params: { class_name: cls, section: sec }});
      setStudents(s.data);
      try {
        const r = await api.get(`/marks/test-type-log/${cls}/${sec}/${testType}`);
        const ttl = r.data || {};
        // Restore filter + max/min ONLY if log has been saved previously
        if (ttl.subjects && ttl.subjects.length > 0) setActiveSubjects(ttl.subjects);
        else setActiveSubjects(allSubjects);
        if (ttl.max_marks) setMaxM(ttl.max_marks);
        if (ttl.min_marks) setMinM(ttl.min_marks);
        setSplitCalc(ttl.is_split_calculation_enabled !== false);
        setGradesOnlyNS(!!ttl.grades_only_non_scholastic);
        setSubjectOverrides(ttl.subject_overrides || {});
        setApprovalStatus(ttl.approval_status || null);
        // Populate marks matrix
        const v = {};
        s.data.forEach(st => {
          const subMap = ttl.marks?.[st.student_id] || {};
          v[st.student_id] = {};
          allSubjects.forEach(sub => { v[st.student_id][sub] = subMap[sub] != null ? String(subMap[sub]) : ""; });
        });
        setVals(v);
      } catch (e) {
        // Fallback: empty state
        setActiveSubjects(allSubjects);
        const v = {};
        s.data.forEach(st => {
          v[st.student_id] = {};
          allSubjects.forEach(sub => { v[st.student_id][sub] = ""; });
        });
        setVals(v);
        setApprovalStatus(null);
      }
    })();
  }, [cls, sec, testType, settings.subjects]);  // eslint-disable-line

  const setCell = (sid, sub, val) => setVals(v => ({
    ...v, [sid]: { ...(v[sid]||{}), [sub]: val }
  }));

  // Compute per-student total + percentage + status — filtered to ACTIVE subjects only
  const rowStats = (sid) => {
    const row = vals[sid] || {};
    let entered = Object.entries(row)
      .filter(([sub,v]) => subjects.includes(sub) && v !== "" && v != null);
    // Split calc → scholastic subjects only drive Total/%/Pass-Fail.
    if (splitCalc && hasSeg) entered = entered.filter(([sub]) => isScholastic(sub));
    if (entered.length === 0) return null;
    const total = entered.reduce((a, [,v]) => a + (parseFloat(v) || 0), 0);
    const max = maxM * entered.length;
    const pct = max ? (total / max) * 100 : 0;
    const failed = entered.some(([,v]) => (parseFloat(v) || 0) < parseFloat(minM));
    return { total, max, pct, failed };
  };

  const save = async () => {
    if (saving) return;
    if (!cls || !sec) return toast.error("Pick class & section first");
    if (subjects.length === 0) return toast.error("Select at least one subject");
    setSaving(true);
    // Build marks matrix restricted to active subjects
    const marksMatrix = {};
    const partBSet = new Set(settings.subjects_part_b || []);
    Object.entries(vals).forEach(([sid, row]) => {
      const rowOut = {};
      Object.entries(row).forEach(([sub, m]) => {
        if (!subjects.includes(sub)) return;
        if (m === "" || m == null) return;
        // Iter67 FIX: grades-only non-scholastic cells hold a LETTER grade (A/B/C).
        // parseFloat("A") → NaN → serialised as null → backend skipped the write,
        // so SA1 Part-B grades silently vanished. Send the raw grade string; the
        // backend coerces numerics via float() and falls back to grade for letters.
        if (gradesOnlyNS && partBSet.has(sub)) {
          rowOut[sub] = String(m).trim();
        } else {
          const n = parseFloat(m);
          rowOut[sub] = Number.isNaN(n) ? String(m).trim() : n;
        }
      });
      if (Object.keys(rowOut).length > 0) marksMatrix[sid] = rowOut;
    });
    try {
      const r = await api.put("/marks/test-type-log", {
        test_type: testType, class_name: cls, section: sec,
        subjects: subjects,
        max_marks: parseFloat(maxM) || 100,
        min_marks: parseFloat(minM) || 0,
        is_split_calculation_enabled: splitCalc,
        grades_only_non_scholastic: gradesOnlyNS,
        subject_overrides: Object.fromEntries(
          Object.entries(subjectOverrides).filter(([sub]) => subjects.includes(sub))
            .map(([sub, o]) => [sub, {
              max_marks: parseFloat(o.max_marks) || (parseFloat(maxM) || 100),
              min_marks: parseFloat(o.min_marks) || (parseFloat(minM) || 0),
            }])),
        marks: marksMatrix,
      });
      setApprovalStatus(r.data.approval_status || null);
      const saved = r.data.saved || 0;
      toast.success(`Saved · ${saved} marks committed for ${testType}`);
    } catch (err) {
      toast.error(errText(err, "Save failed"));
    }
    setSaving(false);
  };

  const pushForApproval = async () => {
    if (!cls || !sec) return toast.error("Pick class & section first");
    try {
      await api.post("/marks/test-type-log/push-approval",
        { test_type: testType, class_name: cls, section: sec, kind: pushKind });
      setApprovalStatus("pending_approval");
      toast.success(`Pushed for approval as ${pushKind === "marks_card" ? "Marks Card" : "Report Card"}`);
    } catch (e) { toast.error(errText(e, "Push failed")); }
  };

  const printBulkMarksCards = async () => {
    if (approvalStatus !== "approved") {
      return toast.error("Bulk print unlocks after Admin/Facilitator approves this Test Type");
    }
    try {
      const r = await api.post("/marks-cards/bulk",
        { test_type: testType, class_name: cls, section: sec },
        { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `MarksCards_${cls}${sec}_${testType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Bulk Marks Cards downloaded");
    } catch (err) { toast.error(errText(err, "Failed")); }
  };

  // Iter49 — Bulk Generate Reports (mirrors the button on the Report Cards tab)
  const printBulkReports = async () => {
    if (!cls || !sec) return toast.error("Pick class & section first");
    try {
      const r = await api.post("/report-cards/generate",
        { class_name: cls, section: sec }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `ReportCards_${cls}${sec}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Bulk Report Cards downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const printSummary = async () => {
    if (!cls || !sec) return toast.error("Pick class + section first");
    try {
      const r = await api.post("/marks/test-summary-pdf",
        { test_type: testType, class_name: cls, section: sec,
          subjects: subjects.length > 0 ? subjects : null },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MarksSummary_${cls}-${sec}_${testType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Marks summary PDF downloaded");
    } catch (err) { toast.error(errText(err, "Failed")); }
  };

  const statusBadge = () => {
    if (!approvalStatus) return null;
    const cls = {
      "pending": "bg-[#FDE047]",
      "pending_approval": "bg-[#FDBA74]",
      "approved": "bg-[#86EFAC]",
    }[approvalStatus] || "bg-white";
    const label = {
      "pending": "SAVED",
      "pending_approval": "AWAITING APPROVAL",
      "approved": "APPROVED",
    }[approvalStatus] || approvalStatus.toUpperCase();
    return <span className={`nb-badge ${cls} ml-2`} data-testid="m-approval-status">{label}</span>;
  };

  return (
    <div className="space-y-5">
      <div className="nb-card p-5 flex flex-wrap gap-3 items-end">
        {/* Iter19: Type dropdown (half-width) BEFORE Grade & Section */}
        <div><label className="nb-label">Test Type</label>
          <select className="nb-input mt-1 w-32" value={testType} onChange={e=>setTestType(e.target.value)} data-testid="m-testtype">
            {settings.test_types?.map(t=><option key={t} value={t}>{t}</option>)}
          </select></div>
        {/* Iter50 — Grade & Section locked UNLESS override is active. */}
        {(() => {
          const unlocked = profile.override_expires_at &&
                           new Date(profile.override_expires_at) > new Date();
          const lockCls = !!profile.class_name && !unlocked;
          const lockSec = !!profile.section && !unlocked;
          return (<>
        <div><label className="nb-label flex items-center gap-1">
          {lockCls ? <><Lock size={11}/>Grade</> : "Grade"}</label>
          <select className="nb-input mt-1 w-32" value={cls}
                  onChange={e=>setCls(e.target.value)}
                  disabled={lockCls}
                  title={lockCls ? "Locked to your assigned class"
                        : unlocked ? `Temporarily unlocked until ${new Date(profile.override_expires_at).toLocaleString()}`
                        : ""}
                  data-testid="m-class">
            <option value="">--</option>{settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className="nb-label flex items-center gap-1">
          {lockSec ? <><Lock size={11}/>Section</> : "Section"}</label>
          <select className="nb-input mt-1 w-32" value={sec}
                  onChange={e=>setSec(e.target.value)}
                  disabled={lockSec}
                  title={lockSec ? "Locked to your assigned section"
                        : unlocked ? `Temporarily unlocked until ${new Date(profile.override_expires_at).toLocaleString()}`
                        : ""}
                  data-testid="m-section">
            <option value="">--</option>{settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
          </select></div>
        {unlocked && (
          <div className="nb-badge bg-[#86EFAC] text-[10px]" data-testid="m-override-badge">
            🔓 Override until {new Date(profile.override_expires_at).toLocaleString(undefined,{month:"short",day:"2-digit",hour:"2-digit",minute:"2-digit"})}
          </div>
        )}
          </>);
        })()}
        <div className="relative"><label className="nb-label">Subjects</label>
          <button type="button"
                  onClick={()=>setSubjectPickerOpen(o=>!o)}
                  className="nb-input mt-1 text-left w-44 flex items-center justify-between"
                  data-testid="m-subjects-picker">
            <span className="truncate">
              {activeSubjects.length === 0 || activeSubjects.length === allSubjects.length
                ? "All subjects"
                : `${activeSubjects.length}/${allSubjects.length} selected`}
            </span>
            <span className="ml-2">▾</span>
          </button>
          {subjectPickerOpen && (
            <div className="absolute z-20 mt-1 nb-card p-2 bg-white w-56 max-h-64 overflow-y-auto"
                 data-testid="m-subjects-dropdown">
              <div className="flex justify-between mb-1">
                <button type="button" className="text-[10px] underline"
                        onClick={()=>setActiveSubjects(allSubjects)}
                        data-testid="m-subjects-all">Select all</button>
                <button type="button" className="text-[10px] underline"
                        onClick={()=>setActiveSubjects([])}
                        data-testid="m-subjects-none">Clear</button>
              </div>
              {allSubjects.map(sub => (
                <label key={sub} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer">
                  <input type="checkbox"
                         checked={activeSubjects.includes(sub)}
                         onChange={e => {
                           setActiveSubjects(prev =>
                             e.target.checked ? [...prev, sub] : prev.filter(x=>x!==sub));
                         }}
                         data-testid={`m-subj-cb-${sub}`}/>
                  {sub}
                </label>
              ))}
            </div>
          )}</div>
        <div><label className="nb-label">Max Marks</label>
          <input type="number" className="nb-input mt-1 w-28 mono" value={maxM}
                 onChange={e=>setMaxM(e.target.value)} data-testid="m-max"/></div>
        <div><label className="nb-label">Passing Minimum</label>
          <div className="flex flex-col gap-1 mt-1">
            <input type="number" className="nb-input w-28 mono" value={minM}
                   onChange={e=>setMinM(e.target.value)} data-testid="m-min"/>
            <div className="flex border-2 border-black rounded overflow-hidden w-28" data-testid="m-marks-grades-toggle">
              <button type="button" onClick={()=>setGradesOnlyNS(false)}
                className={`flex-1 px-1 py-0.5 text-[11px] font-bold ${!gradesOnlyNS?"bg-black text-white":"bg-white"}`} data-testid="m-toggle-marks">Marks</button>
              <button type="button" onClick={()=>setGradesOnlyNS(true)}
                className={`flex-1 px-1 py-0.5 text-[11px] font-bold ${gradesOnlyNS?"bg-black text-white":"bg-white"}`} data-testid="m-toggle-grades">Grades</button>
            </div>
          </div></div>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer" data-testid="m-grades-only-ns">
            <input type="checkbox" checked={gradesOnlyNS} onChange={e=>setGradesOnlyNS(e.target.checked)}/>
            Use Grades Only for Non-Scholastic
          </label>
          <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer" data-testid="m-split-calc">
            <input type="checkbox" checked={splitCalc} onChange={e=>setSplitCalc(e.target.checked)}/>
            Split Calculation (Scholastic-only totals)
          </label>
        </div>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          {statusBadge()}
          <button data-testid="m-print-summary" onClick={printSummary}
                  className="nb-btn nb-btn-secondary"
                  title="Print an A4-landscape summary of all subjects with a parent-sign column">
            <Printer size={16}/> Print Summary
          </button>
          <button data-testid="m-save" onClick={save} disabled={saving} className="nb-btn"><Save size={16}/>{saving?"Saving…":"Save Marks"}</button>
          <button data-testid="m-push-approval" onClick={pushForApproval}
                  className="nb-btn nb-btn-tertiary"
                  disabled={approvalStatus === "pending_approval"}>
            <Send size={16}/> Push for Approval
          </button>
          <button data-testid="m-bulk-print" onClick={printBulkMarksCards}
                  className="nb-btn"
                  disabled={approvalStatus !== "approved"}
                  title={approvalStatus === "approved"
                    ? "Bulk print all Marks Cards for this Test Type"
                    : "Push for approval first, then wait for Admin/Facilitator to approve"}>
            <Printer size={16}/> Print Bulk Marks Cards
          </button>
          {/* Iter49 — mirrors the "Bulk Generate Reports" from the Report Cards tab */}
          <button data-testid="m-bulk-reports" onClick={printBulkReports}
                  className="nb-btn nb-btn-tertiary"
                  disabled={!cls || !sec}
                  title="Consolidated multi-test report cards for the whole class">
            <Printer size={16}/> Bulk Generate Reports
          </button>
        </div>
      </div>
      {/* Iter68 — Subject-specific Max/Passing overrides (up to 3 subjects) */}
      <div className="nb-card p-4 bg-[#FEF9C3]" data-testid="m-overrides-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-bold display text-sm">Override Max Marks / Passing — up to 3 subjects</div>
          <span className="text-[11px] opacity-70">Unlisted subjects inherit Max {maxM} / Pass {minM}. Totals & % recompute automatically.</span>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          {[0,1,2].map(i => {
            const keys = Object.keys(subjectOverrides);
            const selSub = keys[i] || "";
            const o = selSub ? subjectOverrides[selSub] : {};
            const setRow = (nextSub, patch) => {
              setSubjectOverrides(prev => {
                const copy = {...prev};
                const prevSub = keys[i];
                if (prevSub && prevSub !== nextSub) delete copy[prevSub];
                if (!nextSub) { if (prevSub) delete copy[prevSub]; return copy; }
                copy[nextSub] = { ...(copy[nextSub]||{max_marks:maxM,min_marks:minM}), ...patch };
                return copy;
              });
            };
            return (
              <div key={i} className="border-2 border-black rounded-md p-2 bg-white space-y-1.5">
                <select className="nb-input w-full text-xs" value={selSub}
                        onChange={e=>setRow(e.target.value, {})} data-testid={`m-override-subject-${i}`}>
                  <option value="">— Select subject —</option>
                  {subjects.filter(su => su===selSub || !keys.includes(su)).map(su =>
                    <option key={su} value={su}>{su}</option>)}
                </select>
                <div className="flex gap-2">
                  <label className="flex-1 text-[10px] font-bold">Max
                    <input type="number" className="nb-input w-full mono text-xs" disabled={!selSub}
                           value={o.max_marks ?? ""} placeholder={String(maxM)}
                           onChange={e=>setRow(selSub, {max_marks:e.target.value})}
                           data-testid={`m-override-max-${i}`}/>
                  </label>
                  <label className="flex-1 text-[10px] font-bold">Pass Min
                    <input type="number" className="nb-input w-full mono text-xs" disabled={!selSub}
                           value={o.min_marks ?? ""} placeholder={String(minM)}
                           onChange={e=>setRow(selSub, {min_marks:e.target.value})}
                           data-testid={`m-override-min-${i}`}/>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Iter49 — Split marks matrix into 2 visually distinct sections:
                    Section A → Scholastic (subjects_part_a)
                    Section B → Co-Scholastic / Non-Scholastic (subjects_part_b) */}
      {(() => {
        const scholastic   = subjects.filter(s => (settings.subjects_part_a||[]).includes(s));
        const coScholastic = subjects.filter(s => (settings.subjects_part_b||[]).includes(s));
        const legacy       = subjects.filter(s => !scholastic.includes(s) && !coScholastic.includes(s));
        const sectionA     = scholastic.length ? scholastic : legacy;
        const sectionB     = coScholastic;
        const renderTable = (label, cols, testid, isNS=false) => (cols.length === 0) ? null : (
          <div className="nb-card p-0 overflow-x-auto" data-testid={testid}>
            <div className="px-6 py-2 border-b-2 border-black bg-[#DBEAFE] font-bold display text-sm">
              {label} — {cols.length} {cols.length === 1 ? "subject/activity" : "subjects/activities"}
            </div>
            <table className="nb-table">
              <thead><tr>
                <th className="min-w-[3rem]">Roll</th><th className="min-w-[10rem]">Name</th>
                {cols.map(sub=>(
                  <th key={sub} className="text-center text-xs whitespace-nowrap px-2" style={{minWidth: "5.5rem"}}>
                    {sub}<div className="text-[10px] mono text-slate-500">/{maxM||100}</div>
                  </th>
                ))}
                <th className="text-right text-xs">Total</th>
                <th className="text-right text-xs">%</th>
                <th className="text-center text-xs">Result</th>
              </tr></thead>
              <tbody>
                {students.map(s=>{
                  const stats = rowStats(s.student_id);
                  return (
                    <tr key={s.student_id}>
                      <td className="mono">{s.roll_no}</td><td className="font-bold">{s.name}</td>
                      {cols.map(sub=>{
                        const v = vals[s.student_id]?.[sub];
                        const num = parseFloat(v);
                        const fail = v !== "" && !isNaN(num) && num < parseFloat(minM);
                        return (
                          <td key={sub} className="px-2" style={{minWidth: "5.5rem"}}>
                            {isNS && gradesOnlyNS ? (
                              <select className="nb-input text-center text-sm font-bold"
                                      style={{width: "5rem", minWidth: "5rem"}}
                                      value={v ?? ""}
                                      onChange={e=>setCell(s.student_id, sub, e.target.value)}
                                      data-testid={`m-${s.student_id}-${sub}`}>
                                <option value="">—</option>
                                {gradeTiers.map(g=><option key={g} value={g}>{g}</option>)}
                              </select>
                            ) : (
                              <input type="number" min="0" max={maxM}
                                   className={`nb-input text-center text-base font-bold ${fail ? "bg-[#FCA5A5]" : ""}`}
                                   style={{width: "5rem", minWidth: "5rem"}}
                                   value={v ?? ""}
                                   onChange={e=>setCell(s.student_id, sub, e.target.value)}
                                   data-testid={`m-${s.student_id}-${sub}`}/>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-right mono text-sm">
                        {stats ? `${stats.total.toFixed(0)}/${stats.max.toFixed(0)}` : "—"}
                      </td>
                      <td className="text-right mono font-bold" data-testid={`m-pct-${s.student_id}`}>
                        {stats ? `${stats.pct.toFixed(1)}%` : "—"}
                      </td>
                      <td className="text-center">
                        {stats ? (
                          <span className={`nb-badge ${stats.failed ? "bg-[#FCA5A5]" : "bg-[#86EFAC]"}`}>
                            {stats.failed ? "FAIL" : "PASS"}
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {students.length===0 && <tr><td colSpan={5+cols.length} className="text-center py-6 text-slate-500">Pick grade & section</td></tr>}
              </tbody>
            </table>
          </div>
        );
        return (
          <div className="space-y-4">
            {renderTable("Section A · Scholastic Subjects", sectionA, "m-table-scholastic")}
            {renderTable("Section B · Co-Scholastic / Non-Scholastic Activities", sectionB, "m-table-co-scholastic", true)}
            {sectionA.length === 0 && sectionB.length === 0 && (
              <div className="nb-card p-6 text-center text-slate-500 text-sm">Admin has not configured subjects yet</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ---------- Marks Cards — test-wise summary + submit-for-approval workflow ---------- */
function MarksCards({ profile, settings }) {
  const [cls, setCls] = useState(profile.class_name || "");
  const [sec, setSec] = useState(profile.section || "");
  const [testType, setTestType] = useState(settings.test_types?.[0] || "FA1");
  const [students, setStudents] = useState([]);
  const [byStudent, setByStudent] = useState({});   // student_id -> [marks]
  const [note, setNote] = useState("");
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(()=>{ setTestType(settings.test_types?.[0] || "FA1"); }, [settings.test_types]);

  const loadSubs = () => api.get("/marks/submissions", { params: { mine: true }}).then(r=>setSubs(r.data));

  useEffect(()=>{
    (async () => {
      if (!cls || !sec) { setStudents([]); setByStudent({}); return; }
      const s = await api.get("/students", { params: { class_name: cls, section: sec }});
      setStudents(s.data);
      const acc = {};
      for (const st of s.data) {
        const r = await api.get(`/marks/student/${st.student_id}`);
        acc[st.student_id] = r.data.filter(m=>m.exam===testType);
      }
      setByStudent(acc);
    })();
    loadSubs();
  }, [cls, sec, testType]);   // eslint-disable-line

  const summary = students.map(s => {
    const marks = byStudent[s.student_id] || [];
    const total = marks.reduce((a,m)=>a+(m.marks||0), 0);
    const max = marks.reduce((a,m)=>a+(m.max_marks||0), 0);
    const min = marks.length ? Math.max(...marks.map(m=>m.min_marks||0)) : 0;
    const pct = max ? +(total/max*100).toFixed(1) : 0;
    const failed = marks.some(m => (m.marks||0) < (m.min_marks||0));
    return { ...s, marks, total, max, pct, failed, min };
  });
  const withMarks = summary.filter(x => x.marks.length > 0);

  const submitForApproval = async () => {
    if (withMarks.length === 0) return toast.error("No marks recorded for this class/section/test");
    if (!window.confirm(`Submit ${withMarks.length} student report(s) for admin approval?`)) return;
    setBusy(true);
    try {
      await api.post("/marks/submissions", { test_type: testType, class_name: cls, section: sec, note });
      toast.success(`Submitted ${withMarks.length} report(s) — awaiting admin approval`);
      setNote(""); loadSubs();
    } catch (err) { toast.error(errText(err, "Failed")); }
    setBusy(false);
  };

  const printSummary = async () => {
    if (!cls || !sec) return toast.error("Pick class & section first");
    setBusy(true);
    try {
      const r = await api.post("/marks/test-summary-pdf",
        { test_type: testType, class_name: cls, section: sec, subject: profile.subject || null },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TestSummary_${cls}-${sec}_${testType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Summary PDF downloaded — hand over to parents at PTM");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to build PDF"); }
    setBusy(false);
  };

  const grade = (pct) => pct>=90?"A+":pct>=80?"A":pct>=70?"B":pct>=60?"C":pct>=50?"D":"E";

  return (
    <div className="space-y-5">
      <div className="nb-card p-5 bg-[#93C5FD] flex flex-wrap gap-3 items-end">
        <div>
          <div className="display text-xl font-bold flex items-center gap-2"><ClipboardCheck size={18}/>Report Cards</div>
          <p className="text-sm mt-1">Multi-test summary pulled from approved Marks Cards entries. Bulk-generate the class report cards in one click.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2 items-end">
          <label><div className="nb-label">Grade</div>
            <select className="nb-select mt-1 w-24" value={cls} onChange={e=>setCls(e.target.value)} data-testid="mc-class">
              <option value="">—</option>{settings.grades?.map(g=><option key={g} value={g}>{g}</option>)}
            </select></label>
          <label><div className="nb-label">Section</div>
            <select className="nb-select mt-1 w-20" value={sec} onChange={e=>setSec(e.target.value)} data-testid="mc-section">
              <option value="">—</option>{settings.sections?.map(s=><option key={s} value={s}>{s}</option>)}
            </select></label>
          <label><div className="nb-label">Test Type</div>
            <select className="nb-select mt-1 w-28" value={testType} onChange={e=>setTestType(e.target.value)} data-testid="mc-testtype">
              {settings.test_types?.map(t=><option key={t} value={t}>{t}</option>)}
            </select></label>
        </div>
      </div>

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex flex-wrap items-center justify-between gap-2">
          <h3 className="display text-lg font-bold">Summary — {testType} · {cls || "—"}-{sec || "—"} ({withMarks.length}/{summary.length} with marks)</h3>
          <div className="flex gap-2 items-center">
            <input className="nb-input text-sm w-64" placeholder="Optional note for admin"
                   value={note} onChange={e=>setNote(e.target.value)} data-testid="mc-note"/>
            <button disabled={busy || withMarks.length===0} onClick={printSummary}
                    className="nb-btn nb-btn-secondary" data-testid="mc-print-summary"
                    title="Print an A4 test-summary sheet with a parent-sign column">
              <Printer size={14}/> Print Summary
            </button>
            <button disabled={busy || withMarks.length===0} onClick={submitForApproval}
                    className="nb-btn" data-testid="mc-submit">
              <Send size={14}/> Send for Approval
            </button>
            <button disabled={busy || !cls || !sec} onClick={async () => {
              // Iter21 — Bulk Generate Report Cards for the class
              setBusy(true);
              try {
                const r = await api.post("/report-cards/generate",
                  { class_name: cls, section: sec }, { responseType: "blob" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(r.data);
                a.download = `ReportCards_${cls}${sec}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                toast.success("Bulk Report Cards PDF downloaded");
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              setBusy(false);
            }} className="nb-btn nb-btn-tertiary" data-testid="mc-bulk-reports">
              <Printer size={14}/> Bulk Generate Reports
            </button>
          </div>
        </div>
        <table className="nb-table">
          <thead><tr><th>Roll</th><th>Name</th><th>Total</th><th>Max</th><th>%</th><th>Grade</th><th>Status</th></tr></thead>
          <tbody>
            {summary.map(s=>(
              <tr key={s.student_id}>
                <td className="mono">{s.roll_no}</td>
                <td className="font-bold">{s.name}</td>
                <td className="mono">{s.marks.length ? s.total : "—"}</td>
                <td className="mono">{s.marks.length ? s.max : "—"}</td>
                <td className="mono font-bold" data-testid={`mc-pct-${s.student_id}`}>{s.marks.length ? `${s.pct}%` : "—"}</td>
                <td><span className="nb-badge bg-[#C4B5FD]">{s.marks.length ? grade(s.pct) : "—"}</span></td>
                <td>
                  {s.marks.length === 0 ? <span className="text-slate-400 text-xs">No marks</span> :
                    s.failed ? <span className="nb-badge bg-[#FCA5A5]">FAIL</span> :
                              <span className="nb-badge bg-[#86EFAC]">PASS</span>}
                </td>
              </tr>
            ))}
            {summary.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Pick grade &amp; section</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="nb-card p-0 overflow-hidden" data-testid="mc-my-submissions">
        <div className="px-6 py-3 border-b-2 border-black bg-white"><h3 className="display text-lg font-bold">My Submissions</h3></div>
        <table className="nb-table">
          <thead><tr><th>Submitted</th><th>Class</th><th>Test</th><th>Students</th><th>Status</th><th>Admin Note</th></tr></thead>
          <tbody>
            {subs.map(s=>(
              <tr key={s.submission_id} data-testid={`mc-sub-${s.submission_id}`}>
                <td className="mono text-xs">{new Date(s.submitted_at).toLocaleString()}</td>
                <td>{s.class_name}-{s.section}</td>
                <td>{s.test_type}</td>
                <td className="mono">{s.student_count}</td>
                <td>
                  <span className={`nb-badge ${s.status==="approved"?"bg-[#86EFAC]":s.status==="rejected"?"bg-[#FCA5A5]":"bg-[#FDE047]"}`}>
                    {s.status.toUpperCase()}
                  </span>
                </td>
                <td className="text-xs">{s.decision_note || (s.status==="pending" ? "Awaiting admin review" : "—")}</td>
              </tr>
            ))}
            {subs.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No submissions yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==================================================================
   Iter29 — Educator Ponderr (read-only curriculum + private notes)
   ================================================================== */
function EducatorPonderr() {
  const [meta, setMeta] = useState({board_code:null, subjects:[]});
  const [grade, setGrade] = useState("");
  const [active, setActive] = useState(null); // {curriculum_id, subject_name, grade}
  const [curriculum, setCurriculum] = useState(null);
  const [openChapter, setOpenChapter] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Iter49 — restrict "My Courses" to the educator's assigned subject (School Admin → Educators tab).
  const [mySubject, setMySubject] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(()=>{
    api.get("/educator/lms/subjects").then(r => setMeta(r.data));
    api.get("/educator/me").then(r => setMySubject((r.data?.subject || "").trim()))
                          .catch(()=> setMySubject(""));
  }, []);

  const grades = [...new Set(meta.subjects.map(s => s.grade))].sort();
  // Base filter: by grade if picked.
  const byGrade = meta.subjects.filter(s => !grade || s.grade === grade);
  // Subject filter: keep courses whose subject_name matches educator's assigned subject.
  const mine = mySubject
    ? byGrade.filter(s => (s.subject_name || "").toLowerCase() === mySubject.toLowerCase())
    : byGrade;
  const filtered = showAll ? byGrade : mine;

  const openSubject = async (s) => {
    setActive(s); setOpenChapter(null); setLessons([]); setActiveLesson(null);
    try {
      const r = await api.get(`/educator/lms/curriculum/${s.curriculum_id}`);
      setCurriculum(r.data);
    } catch { toast.error("Failed to load"); }
  };
  const openChapterHandler = async (ch) => {
    setOpenChapter(ch); setActiveLesson(null);
    try {
      const r = await api.get(`/educator/lms/lessons/${ch.chapter_id}`);
      setLessons(r.data);
    } catch { toast.error("Failed"); }
  };
  const openLesson = async (l) => {
    setActiveLesson(l);
    try {
      const r = await api.get(`/educator/lms/notes/${l.lesson_id}`);
      setNote(r.data?.notes_html || "");
    } catch { setNote(""); }
  };
  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.post("/educator/lms/notes", { lesson_id: activeLesson.lesson_id, notes_html: note });
      toast.success("Note saved (visible only to your students)");
    } catch (err) { toast.error(errText(err, "Failed")); }
    setSavingNote(false);
  };
  const delNote = async () => {
    if (!window.confirm("Delete your note?")) return;
    try {
      await api.delete(`/educator/lms/notes/${activeLesson.lesson_id}`);
      setNote(""); toast.success("Note deleted");
    } catch { toast.error("Failed"); }
  };

  if (!meta.board_code) {
    return <div className="nb-card p-6 bg-[#FDE047]">
      <div className="display text-xl font-bold">Ponderr LMS</div>
      <p className="text-sm mt-2">Your school administrator needs to configure a board first.</p>
    </div>;
  }

  if (activeLesson) {
    return <div className="space-y-4">
      <div className="flex gap-2 items-center text-xs mono">
        <button onClick={()=>{ setActive(null); setCurriculum(null); setOpenChapter(null); setLessons([]); setActiveLesson(null); }}
                className="underline">Subjects</button>
        <span>›</span>
        <button onClick={()=>{ setActiveLesson(null); }} className="underline">{active.subject_name}</button>
        <span>›</span>
        <span>{openChapter.title} › {activeLesson.title}</span>
      </div>
      <EducatorLessonView lesson={activeLesson}/>
      <div className="nb-card p-4 bg-[#FDE047] space-y-2" data-testid="edu-note-editor">
        <div className="display text-lg font-bold">Your Private Note for this Lesson</div>
        <p className="text-xs">Visible only to your own students at this school.</p>
        <div className="flex gap-1 mb-1 text-xs">
          {[["<b>","</b>","B"],["<i>","</i>","I"],["<h3>","</h3>","H"],
           ["<ul><li>","</li></ul>","•"],["<br/>","","↵"]].map(([o,c,lbl])=>(
            <button key={lbl} type="button" className="nb-btn nb-btn-ghost px-2 py-0.5"
                    onClick={()=>setNote(x => x + o + c)}>{lbl}</button>
          ))}
        </div>
        <textarea className="nb-textarea" rows={6} placeholder="Add teaching context, example problems, or reminders…"
                  value={note} onChange={e=>setNote(e.target.value)} data-testid="edu-note-textarea"/>
        {note && (
          <div className="nb-card-sm p-3 bg-white text-sm prose max-w-none"
               dangerouslySetInnerHTML={{__html: note}}/>
        )}
        <div className="flex gap-2">
          <button onClick={saveNote} disabled={savingNote} className="nb-btn" data-testid="edu-note-save">
            {savingNote ? "Saving…" : "Save Note"}
          </button>
          {note && <button onClick={delNote} className="nb-btn nb-btn-danger">Delete Note</button>}
        </div>
      </div>
      <button onClick={()=>setActiveLesson(null)} className="nb-btn nb-btn-ghost">← Back</button>
    </div>;
  }

  if (openChapter) {
    return <div className="space-y-4">
      <div className="flex gap-2 items-center text-xs mono">
        <button onClick={()=>{ setActive(null); setCurriculum(null); setOpenChapter(null); setLessons([]); }}
                className="underline">Subjects</button>
        <span>›</span>
        <button onClick={()=>{ setOpenChapter(null); setLessons([]); }} className="underline">{active.subject_name}</button>
        <span>›</span>
        <span>{openChapter.title}</span>
      </div>
      <div className="nb-card p-4 bg-[#93C5FD]">
        <div className="nb-label">Chapter {openChapter.order}</div>
        <h3 className="display text-xl font-bold">{openChapter.title}</h3>
      </div>
      {lessons.length === 0 ? (
        <div className="nb-card p-6 text-center text-slate-500 text-sm">No lessons yet.</div>
      ) : (
        <div className="space-y-2">
          {lessons.map(l => (
            <button key={l.lesson_id} onClick={()=>openLesson(l)}
                    className="nb-card w-full p-3 text-left hover:bg-[#FDE047]">
              <span className="nb-badge bg-white text-xs mr-2">{l.kind}</span>
              <span className="font-bold">{l.order}. {l.title}</span>
              {l.description && <div className="text-xs text-slate-600 mt-1">{l.description}</div>}
            </button>
          ))}
        </div>
      )}
      <button onClick={()=>{ setOpenChapter(null); setLessons([]); }} className="nb-btn nb-btn-ghost">← Back</button>
    </div>;
  }

  if (active && curriculum) {
    return <div className="space-y-4">
      <div className="nb-card p-4 bg-[#86EFAC]">
        <div className="nb-label">{meta.board_code} · {active.grade}</div>
        <h3 className="display text-2xl font-bold">{active.subject_name}</h3>
      </div>
      <div className="space-y-2">
        {(curriculum.chapters||[]).map(ch => (
          <button key={ch.chapter_id} onClick={()=>openChapterHandler(ch)}
                  className="nb-card w-full p-3 text-left hover:bg-[#FDE047]">
            <div className="font-bold">{ch.order}. {ch.title}</div>
          </button>
        ))}
      </div>
      <button onClick={()=>{ setActive(null); setCurriculum(null); }} className="nb-btn nb-btn-ghost">← Back to subjects</button>
    </div>;
  }

  return <div className="space-y-4">
    <div className="nb-card p-4 bg-[#C4B5FD]">
      <div className="nb-label">Ponderr LMS · {meta.board_code}</div>
      <div className="display text-2xl font-bold">Curriculum Library</div>
      <p className="text-xs mt-1">Read-only. Attach your own private notes on each lesson — visible only to your students.</p>
    </div>
    <div className="flex gap-2 items-center flex-wrap">
      <div className="nb-label">Grade:</div>
      <select className="nb-select" value={grade} onChange={e=>setGrade(e.target.value)}
              data-testid="edu-grade-filter">
        <option value="">All</option>
        {grades.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      {/* Iter49 — subject filter based on assigned subject */}
      {mySubject && (
        <label className="flex items-center gap-1 text-xs ml-2">
          <input type="checkbox" checked={showAll}
                 onChange={e=>setShowAll(e.target.checked)}
                 data-testid="edu-show-all-subjects"/>
          Show all subjects (default: only my subject <b>{mySubject}</b>)
        </label>
      )}
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {filtered.map(s => (
        <button key={s.curriculum_id} onClick={()=>openSubject(s)}
                className="nb-card p-4 text-left hover:bg-[#FDE047]">
          <div className="nb-label">{s.grade} · {s.subject_code}</div>
          <div className="display text-lg font-bold mt-1">{s.subject_name}</div>
          <div className="text-xs mt-2 text-slate-600">{s.chapter_count} chapter(s)</div>
        </button>
      ))}
      {filtered.length===0 && <div className="col-span-3 nb-card p-6 text-center text-slate-500 text-sm">No curriculums for this filter yet.</div>}
    </div>
  </div>;
}

function EducatorLessonView({ lesson }) {
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(()=>{
    let revoke;
    if ((lesson.kind==="pdf"||lesson.kind==="audio"||lesson.kind==="video_upload") && lesson.storage_path) {
      (async () => {
        try {
          const r = await api.get(`/lms/files/serve/${lesson.storage_path}`, { responseType:"blob" });
          const url = URL.createObjectURL(r.data); setBlobUrl(url); revoke = url;
        } catch { /* ignore */ }
      })();
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [lesson]);
  const ytEmbed = (url) => { const m = (url||"").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/); return m ? `https://www.youtube.com/embed/${m[1]}` : url; };
  return <div className="nb-card p-4">
    <h4 className="display text-xl font-bold">{lesson.title}</h4>
    {lesson.description && <p className="text-sm text-slate-700 mt-1">{lesson.description}</p>}
    <div className="mt-3">
      {lesson.kind==="notes" && <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{__html: lesson.notes_html || "<i>No content.</i>"}}/>}
      {lesson.kind==="html" && <iframe title={lesson.title} srcDoc={lesson.html_content} sandbox="allow-scripts allow-same-origin" className="w-full rounded border-2 border-black" style={{height:"70vh"}}/>}
      {lesson.kind==="pdf" && blobUrl && <iframe title={lesson.title} src={blobUrl} className="w-full rounded border-2 border-black" style={{height:"75vh"}}/>}
      {lesson.kind==="audio" && blobUrl && <audio src={blobUrl} controls className="w-full"/>}
      {lesson.kind==="video_upload" && blobUrl && <video src={blobUrl} controls className="w-full rounded border-2 border-black" style={{maxHeight:"70vh"}}/>}
      {lesson.kind==="video_youtube" && lesson.youtube_url && (
        <div className="relative w-full" style={{paddingBottom:"56.25%"}}>
          <iframe title={lesson.title} src={ytEmbed(lesson.youtube_url)} className="absolute inset-0 w-full h-full rounded border-2 border-black" allowFullScreen/>
        </div>)}
    </div>
  </div>;
}

/* Educator Review Queue — short-answer attempts */
export function EducatorReviewQueue() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null); // attempt detail
  const load = async () => setRows((await api.get("/educator/lms/review-queue")).data);
  useEffect(()=>{ load(); }, []);

  if (active) return <ReviewAttemptModal attemptSummary={active}
                                           onClose={()=>{ setActive(null); load(); }}/>;

  return <div className="space-y-4" data-testid="edu-review-queue">
    <div className="nb-card p-4 bg-[#FCA5A5]">
      <div className="display text-xl font-bold">Review Queue</div>
      <p className="text-xs">Short-answer submissions from your students awaiting grading.</p>
    </div>
    <div className="space-y-2">
      {rows.length === 0 && <div className="nb-card p-6 text-center text-slate-500 text-sm">Nothing to review right now.</div>}
      {rows.map(r => (
        <div key={r.attempt_id} className="nb-card p-3 flex justify-between items-center gap-2"
             data-testid={`review-row-${r.attempt_id}`}>
          <div className="min-w-0">
            <div className="font-bold">{r.exam?.title || "Exam"}</div>
            <div className="text-xs mono text-slate-600">{r.student_name} · {r.class_name || ""}/{r.section || ""} · submitted {(r.submitted_at||"").slice(0,10)}</div>
            <div className="text-xs">Provisional score: <b>{r.score_pct ?? 0}%</b></div>
          </div>
          <button onClick={()=>setActive(r)} className="nb-btn" data-testid={`review-open-${r.attempt_id}`}>Review</button>
        </div>
      ))}
    </div>
  </div>;
}

function ReviewAttemptModal({ attemptSummary, onClose }) {
  const [att, setAtt] = useState(null);
  const [reviews, setReviews] = useState({}); // {qid: {marks_awarded, comment}}
  useEffect(()=>{
    // Fetch full attempt detail — we need to hit an educator endpoint or reuse student endpoint via user (educator won't have it). Use review queue payload — must include answers.
    (async () => {
      // No dedicated endpoint yet — hit student attempt route as owner. Educator has separate data.
      // Fallback: The review-queue endpoint returned answers via projection? We excluded them. So we need a fetch.
      // Use lms_attempts direct — not exposed. Show provisional payload from summary.
      setAtt(attemptSummary);
    })();
  }, [attemptSummary]);

  // For a simple UX, fetch full attempt via a lightweight endpoint we'll add: skip — use summary.
  const submit = async () => {
    const items = Object.entries(reviews).map(([qid, r]) => ({
      question_id: qid, marks_awarded: r.marks_awarded, comment: r.comment || ""
    }));
    if (!items.length) return toast.error("Score at least one question");
    try {
      await api.post(`/educator/lms/attempts/${attemptSummary.attempt_id}/review`, { items });
      toast.success("Review submitted"); onClose();
    } catch (err) { toast.error(errText(err, "Failed")); }
  };

  if (!att) return null;
  return <div className="space-y-4" data-testid="review-modal">
    <div className="nb-card p-4 bg-[#FDE047]">
      <div className="display text-xl font-bold">Review: {att.exam?.title}</div>
      <div className="text-xs mono">Student: {att.student_name} · Class {att.class_name}/{att.section}</div>
      <p className="text-xs mt-2">This attempt contains short-answer questions. Score each below (max marks per question is stored on the question).</p>
    </div>
    <ReviewQuestionList attemptId={att.attempt_id} reviews={reviews} setReviews={setReviews}/>
    <div className="flex justify-end gap-2">
      <button onClick={onClose} className="nb-btn nb-btn-ghost">Cancel</button>
      <button onClick={submit} className="nb-btn" data-testid="review-submit">Submit Review</button>
    </div>
  </div>;
}

function ReviewQuestionList({ attemptId, reviews, setReviews }) {
  // Fetch full attempt via student endpoint won't work (educator). We use a workaround —
  // the educator can hit a global endpoint. Since we don't have that yet, we present a
  // simple grading grid indexed by question_id + text_answer via inline entry.
  const [detail, setDetail] = useState(null);
  useEffect(()=>{
    (async () => {
      // Educator gets full attempt via review-queue (with answers). We'll re-fetch queue and pick.
      try {
        const r = await api.get("/educator/lms/attempts/" + attemptId + "/detail");
        setDetail(r.data);
      } catch {
        // No endpoint yet — fetch review queue and locate. If still missing, use empty.
        setDetail({answers: [], questions: []});
      }
    })();
  }, [attemptId]);

  if (!detail) return <div className="nb-card p-4 text-xs">Loading…</div>;

  const qmap = Object.fromEntries((detail.questions||[]).map(q => [q.question_id, q]));
  const shortQs = (detail.answers||[]).filter(a => (qmap[a.question_id] || {}).kind === "short_answer" || !a.reviewed);

  if (!shortQs.length) return <div className="nb-card p-4 text-xs">No items require review.</div>;
  return <div className="space-y-3">
    {shortQs.map(a => {
      const q = qmap[a.question_id] || {};
      const cur = reviews[a.question_id] || {};
      const maxMarks = q.marks || 1;
      return <div key={a.question_id} className="nb-card p-3 bg-white space-y-2"
                  data-testid={`review-q-${a.question_id}`}>
        <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{__html: q.question_html || ""}}/>
        <div className="text-xs"><b>Student answer:</b><br/>
          <span className="whitespace-pre-wrap">{a.text_answer || <i className="text-slate-500">(blank)</i>}</span>
        </div>
        {q.model_answer && <div className="text-xs text-slate-600"><b>Rubric:</b> {q.model_answer}</div>}
        <div className="flex items-center gap-2 text-xs">
          <span>Marks awarded (0 – {maxMarks}):</span>
          <input type="number" min="0" max={maxMarks} step="0.5"
                 className="nb-input w-20"
                 value={cur.marks_awarded ?? ""}
                 onChange={e => setReviews(x => ({...x, [a.question_id]: {...cur, marks_awarded: parseFloat(e.target.value) || 0}}))}
                 data-testid={`review-marks-${a.question_id}`}/>
        </div>
        <input className="nb-input text-xs" placeholder="Optional comment"
               value={cur.comment || ""}
               onChange={e => setReviews(x => ({...x, [a.question_id]: {...cur, comment: e.target.value}}))}/>
      </div>;
    })}
  </div>;
}

/* Educator LMS Performance */
export function EducatorLMSPerformance() {
  const [data, setData] = useState({summary:{}, per_student:[], per_chapter:[]});
  const [subject_id, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  useEffect(()=>{ api.get("/global/lms/subjects").then(r=>setSubjects(r.data)); }, []);
  useEffect(()=>{
    const params = {}; if (subject_id) params.subject_id = subject_id;
    api.get("/educator/lms/performance", { params }).then(r => setData(r.data));
  }, [subject_id]);
  return <div className="space-y-4" data-testid="edu-lms-perf">
    <div className="nb-card p-4 bg-[#C4B5FD]">
      <div className="display text-xl font-bold">LMS Performance</div>
      <p className="text-xs">Assessment activity from your class-section.</p>
    </div>
    <div className="flex gap-2 items-center">
      <div className="nb-label">Subject:</div>
      <select className="nb-select" value={subject_id} onChange={e=>setSubject(e.target.value)}>
        <option value="">All</option>
        {subjects.map(s=> <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
      </select>
    </div>
    <div className="grid md:grid-cols-4 gap-3">
      <div className="nb-card p-3 bg-white"><div className="nb-label">Total Attempts</div><div className="display text-2xl font-bold">{data.summary.total_attempts || 0}</div></div>
      <div className="nb-card p-3 bg-[#86EFAC]"><div className="nb-label">Passed</div><div className="display text-2xl font-bold">{data.summary.passed || 0}</div></div>
      <div className="nb-card p-3 bg-[#FDE047]"><div className="nb-label">Avg Score %</div><div className="display text-2xl font-bold">{data.summary.avg_score || 0}</div></div>
      <div className="nb-card p-3 bg-[#FCA5A5]"><div className="nb-label">Pending Review</div><div className="display text-2xl font-bold">{data.summary.pending_review || 0}</div></div>
    </div>
    <div className="nb-card p-0 overflow-x-auto">
      <div className="p-3 border-b-2 border-black font-bold">Per-student</div>
      <table className="nb-table"><thead><tr><th>Student</th><th>Class/Sec</th><th>Attempts</th><th>Passed</th><th>Avg %</th></tr></thead><tbody>
      {data.per_student.map(s => (
        <tr key={s.student_id || s.student_name}>
          <td>{s.student_name}</td>
          <td className="text-xs mono">{s.class_name}/{s.section}</td>
          <td>{s.attempts}</td>
          <td>{s.passed}</td>
          <td>{s.avg_score}</td>
        </tr>
      ))}
      {data.per_student.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No attempts recorded yet</td></tr>}
      </tbody></table>
    </div>
  </div>;
}

